<template>
  <fullscreen-view class="email-creation">
    <template #header>
      <button class="button cancel-button" @click="$emit('closeEmailCreationModal')">cancel</button>
      <input
        :value="emailSubject"
        v-touch:email_subject
        v-has-error:email_subject
        rows="5"
        type="text"
        name="email_subject"
        class="email-subject border-can-error"
        placeholder="email subject"
        @change="updateEmailSubject($event.target.value)"/>
      <span v-if="error" class="error">{{ error }}</span>
      <span v-if="notice" class="notice">{{ notice }}</span>
    </template>
    <template #content>
      <sb-loading-icon v-if="loading" class="loading"/>
      <div v-else id="bee-plugin-container"></div>

      <email-extension-modal
        v-if="extension.showModal"
        :extension="extension"
        :campaign="campaign"
        @rejectExtensionModal="rejectExtensionModal"
        @resolveExtensionModal="resolveExtensionModal"
      />
    </template>
  </fullscreen-view>
</template>

<script>
import { FullscreenView, Touch, HasError } from "@springbig/sb-components";
import { mapGetters } from "vuex";
import { beeAPI } from "../../../services/api/beeAPI.js";
import Bee from "@mailupinc/bee-plugin";
import EmailExtensionModal from "./email-extension-modal.vue"

export default {
  props: {
    campaign: Object,
    email: Object,
    emailSubject: String,
    selectedEmailTemplate: Object
  },

  name: "email-creation",

  directives: {
    Touch, HasError
  },

  data() {
    return {
      error: null,
      loading: false,
      notice: null,
      extension: {
        buttonColor: '#128FF9',   // how the button will look
        buttonText: '',           // what the button will say
        handlers: {},             // contains handler for bee iframe
        options: {},              // contains reward parameters
        showModal: false,         // if the extension modal is open
      },
    }
  },

  async created() {
    const campaignHasOffer = [1, 5, 6].includes(parseInt(this.campaign.extension_type))
    this.extension.buttonText = campaignHasOffer ? "view your offer" : "view your wallet"

    const token = await beeAPI.getAuthToken();

    const beeConfig = {
      uid: this.currentMerchant.uuid,
      container: "bee-plugin-container",
      language: "en-US",
      customCss: `${window.location.origin}/email_template_plugin.css`,
      mergeTags: [
        {
          name: "customer first name",
          value: "%{MEMBER_FIRST_NAME}",
          previewValue: "John"
        },
        {
          name: "member wallet link",
          value: "%{MEMBER_LINK}",
          previewValue: "view your wallet"
        },
        {
          name: "merchant name",
          value: "%{MERCHANT_FULL_NAME}",
          previewValue: "Joe's Store"
        }
      ],
      onSaveAsTemplate: async (jsonLayout) => {
        this.notice = "saving..."
        try {
          let response = await beeAPI.postCreateMerchantEmailTemplate(jsonLayout, this.emailSubject)
          if (response.status == "200") {
            this.error = null
            this.notice = "template created successfully"
          } else {
            this.notice = null
            this.error = response.data
          }
        } catch(e){
          this.notice = null
          this.error = "error, please refresh or contact support";
        }
      },
      onSave: (jsonLayout, htmlLayout) => {
        const params = {
          "layout_json": jsonLayout,
          "layout_html": htmlLayout,
          "campaign_id": this.campaign.id
        }
        beeAPI.putUpdateEmailTemplate(this.campaign.id, jsonLayout, htmlLayout)
          .then(() => {
            // We need to wait 5 seconds in order to get the preview_image generated
            // during these 5 seconds there is a sidekiq worker that builds the preview
            this.loading = true;
            setTimeout(async () => {
              const { specific_campaign_template } = await beeAPI.getEmailTemplates(this.campaign.id);
              this.$emit("update:selectedEmailTemplate", specific_campaign_template);
              this.$emit('closeEmailCreationModal');
              this.$emit('toggleSend');
            }, 5000)
          })
          .catch((error) => {
            const errorText = error?.response?.data?.error
            if (Array.isArray(errorText) && errorText[0] && errorText[0].includes("is invalid html")) {
              this.error = "error, failed to save email content. please make changes and try again"
            } else {
              this.error = "error, please refresh or contact support";
            }
          })
      }
    }
    if(this.useNewCampaign) { // hide the tile for old campaign design users
      const extensionConfig = {
        contentDialog: {
          addOn: {
            handler: (resolve, reject, args) => {
              if (args.contentDialogId == 'trackOfferContent') {
                this.extension.showModal = true
                this.extension.handlers = {
                  resolve, reject, args
                }
              } else {
                reject()
              }
            }
          }
        }
      }
      Object.assign(beeConfig, extensionConfig)
    }
    const beeInstance = new Bee(token);

    beeInstance.start(beeConfig, this.selectedEmailTemplate.layout_json);
  },

  components: {FullscreenView, EmailExtensionModal},

  computed: {
    ...mapGetters(['useNewCampaign', 'currentMerchant'])
  },

  methods: {
    resolveExtensionModal(payload, campaignPayload) {
      this.extension = payload
      const resolve = this.extension.handlers?.resolve;
      if (resolve) {
        resolve({
          "type": "html",
          "value": {
            html:
              `<div align="center">
                <a href="%{EXTENSION_LINK}" target="_blank" style="display:inline-block;color:#ffffff;background-color:${this.extension.buttonColor};border-radius:4px;width:auto;border:1px solid ${this.extension.buttonColor};padding-top:5px;padding-bottom:5px;font-family:Arial;text-align:center;word-break:keep-all;">
                  <span style="padding-left:20px;padding-right:20px;font-size:16px;display:inline-block;letter-spacing:normal;">
                    <span style="font-size: 16px; line-height: 2; word-break: break-word; mso-line-height-alt: 32px;">${this.extension.buttonText}</span>
                  </span>
                </a>
              </div>`,
          }
        });
      }
      this.extension.showModal = false;
      this.$emit("update:campaign", campaignPayload);
    },

    rejectExtensionModal() {
      const reject = this.extension.handlers?.reject;
      if(reject) {reject();}
      this.extension.showModal = false;
    },

    updateEmailSubject(subjectString) {
      this.$emit("updateEmailSubject", "", subjectString);
    }
  },
}
</script>

<style scoped>
.email-creation {
  display: flex;
  flex-direction: column;
}

.email-creation >>> .button.cancel-button {
  background-color: transparent;
  color: #128ff9;
  font-family: "AileronThin";
  font-size: 18px;
  height: 58px;
  line-height: 1em;
  margin: 15px 30px 15px 0;
}

.email-creation >>> header {
  align-items: center;
  background-color: #128ff933;
  display: flex;
  flex-direction: row;
  justify-content: space-between !important;
  height: 100px;
}

.email-creation >>> .content {
  display: flex;
  flex-flow: row wrap;
  height: 100%;
  justify-content: center;
  align-items: center;
  overflow-y: auto;
  width: 100%;
}

#bee-plugin-container {
  flex: 1;
  height: 100%;
}

.email-creation .loading {
  height: auto;
  width: 450px;
}

.email-creation >>> .email-subject {
  width: 500px;
  margin: auto auto auto 10px;
  margin-right: calc(50% - 38px);
}
</style>